import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Delete, FlashOn, UploadFile } from '@mui/icons-material';
import { adminPost, imageUrl } from './CategoryExtraProducts.tsx';
import { uploadOfferImage } from './OfferFields.tsx';
import { IMAGE_SPECS, checkImage, imageSpecText } from '../../utils/imageCheck.tsx';

/**
 * Home page "Flash Sale" banner (app + website): photo on the right, title,
 * subtitle and on / off. The countdown (first running flash sale that ends)
 * and the "Up to X% OFF" badge (best running flash sale discount) are added
 * automatically; the banner is hidden while no flash sale is running.
 */
const FlashBannerSettings: React.FC<{ onMessage: (text: string, kind: 'success' | 'error') => void }> = ({ onMessage }) => {
  const input = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [image, setImage] = useState('');
  const [title, setTitle] = useState('FLASH SALE');
  const [subtitle, setSubtitle] = useState('Limited time. Premium picks.');
  const [live, setLive] = useState<any>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminPost('user/flashBanner', {});
      setEnabled(!!data?.enabled);
      setImage(data?.image || '');
      setTitle(data?.title || 'FLASH SALE');
      setSubtitle(data?.subtitle || '');
      setLive(data);
    } catch (e: any) {
      onMessage(e?.message || 'Could not load the Flash Sale banner', 'error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const pick = async (file?: File | null) => {
    if (!file) return;
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      onMessage('Only JPG, PNG or WebP images are allowed.', 'error');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      onMessage('Image too large - max 2 MB.', 'error');
      return;
    }
    const picked = await checkImage(file, IMAGE_SPECS.flashBannerPhoto); // original or resized
    if (!picked) return;
    setUploading(true);
    try {
      setImage(await uploadOfferImage(picked));
    } catch (e: any) {
      onMessage(e?.message || 'Upload failed', 'error');
    } finally {
      setUploading(false);
      if (input.current) input.current.value = '';
    }
  };

  const save = async () => {
    if (!title.trim()) {
      onMessage('Enter a title for the banner', 'error');
      return;
    }
    setSaving(true);
    try {
      await adminPost('user/saveFlashBanner', { enabled: enabled ? 1 : 0, image, title: title.trim(), subtitle: subtitle.trim() });
      onMessage('Flash Sale banner saved', 'success');
      load();
    } catch (e: any) {
      onMessage(e?.message || 'Could not save the banner', 'error');
    } finally {
      setSaving(false);
    }
  };

  const photo = image ? imageUrl(image) : '';
  const end = live?.end_date ? new Date(live.end_date) : null;

  return (
    <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', mb: 2 }}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#202223', display: 'flex', alignItems: 'center', gap: 1 }}>
              <FlashOn sx={{ color: '#F76338' }} /> Home page Flash Sale banner
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Shown on the app and website home page while a flash sale is running. The countdown and the
              "Up to X% OFF" badge are filled in automatically.
            </Typography>
          </Box>
          <FormControlLabel
            control={<Switch checked={enabled} onChange={e => setEnabled(e.target.checked)} color="warning" />}
            label={enabled ? 'Shown' : 'Hidden'}
          />
        </Box>

        {loading ? (
          <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={26} /></Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' }, gap: 3 }}>
            {/* live preview */}
            <Box sx={{ position: 'relative', borderRadius: '18px', overflow: 'hidden', bgcolor: '#1E2B23', height: 200, color: '#F6EEDF' }}>
              <Box sx={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: '60%' }}>
                {uploading ? (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={24} sx={{ color: '#F6EEDF' }} /></Box>
                ) : photo ? (
                  <img src={photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                ) : (
                  <Box sx={{ height: '100%', background: 'linear-gradient(135deg,#2E4434,#3F5A45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FlashOn sx={{ fontSize: 80, color: 'rgba(246,238,223,0.18)' }} />
                  </Box>
                )}
                <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: '55%', background: 'linear-gradient(90deg,#1E2B23 0%,rgba(30,43,35,.85) 35%,rgba(30,43,35,0) 100%)' }} />
              </Box>
              <Box sx={{ position: 'relative', p: 2.25, height: '100%', width: '55%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxSizing: 'border-box' }}>
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: 20, display: 'flex', alignItems: 'center', gap: .75 }}><FlashOn /> {title || 'FLASH SALE'}</Typography>
                  {!!subtitle && <Typography sx={{ fontSize: 12, opacity: .85 }}>{subtitle}</Typography>}
                </Box>
                <Box sx={{ display: 'flex', gap: .75 }}>
                  {[['02', 'Days'], ['14', 'Hours'], ['37', 'Mins']].map(([v, l]) => (
                    <Box key={l} sx={{ border: '1px solid rgba(246,238,223,.45)', borderRadius: '9px', px: 1, py: .4, textAlign: 'center', minWidth: 44 }}>
                      <Typography sx={{ fontSize: 16, fontWeight: 600, lineHeight: 1.2 }}>{v}</Typography>
                      <Typography sx={{ fontSize: 9, opacity: .8 }}>{l}</Typography>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ alignSelf: 'flex-start', bgcolor: '#F6EEDF', color: '#16211A', borderRadius: '18px', px: 1.75, py: .6, fontSize: 13, fontWeight: 600 }}>Shop Now →</Box>
              </Box>
              {Number(live?.max_discount) > 0 && (
                <Box sx={{ position: 'absolute', top: 10, right: 10, width: 64, height: 64, borderRadius: '50%', bgcolor: '#1E2B23', border: '2px solid #F6EEDF', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
                  <Typography sx={{ fontSize: 8, fontWeight: 600 }}>UP TO</Typography>
                  <Typography sx={{ fontSize: 19, fontFamily: 'Georgia, serif' }}>{live.max_discount}%</Typography>
                  <Typography sx={{ fontSize: 8, fontWeight: 600 }}>OFF</Typography>
                </Box>
              )}
            </Box>

            {/* settings */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: .75 }}>Photo (right side)</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button variant="contained" size="small" startIcon={<UploadFile />} disabled={uploading}
                    onClick={() => input.current?.click()}
                    sx={{ bgcolor: '#202223', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#303030' } }}>
                    {image ? 'Change photo' : 'Upload photo'}
                  </Button>
                  {!!image && (
                    <Button variant="contained" size="small" startIcon={<Delete />} disabled={uploading} onClick={() => setImage('')}
                      sx={{ bgcolor: '#f1f2f4', color: '#202223', textTransform: 'none', fontWeight: 600, boxShadow: 'none', '&:hover': { bgcolor: '#e4e5e7', boxShadow: 'none' } }}>
                      Remove
                    </Button>
                  )}
                </Box>
                <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={e => pick(e.target.files?.[0])} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: .75 }}>
                  {imageSpecText(IMAGE_SPECS.flashBannerPhoto)}
                </Typography>
              </Box>
              <TextField size="small" label="Title" value={title} inputProps={{ maxLength: 40 }} onChange={e => setTitle(e.target.value)} />
              <TextField size="small" label="Subtitle (optional)" value={subtitle} inputProps={{ maxLength: 80 }} onChange={e => setSubtitle(e.target.value)} />
              <Typography variant="caption" color="text.secondary">
                {live?.active && end
                  ? `Live now: ${live.count} flash sale item(s), first one ends ${end.toLocaleString()}${Number(live.max_discount) > 0 ? `, best discount ${live.max_discount}%` : ''}.`
                  : 'No flash sale is running right now - the banner stays hidden until one starts.'}
              </Typography>
              <Box>
                <Button variant="contained" onClick={save} disabled={saving || uploading}
                  sx={{ bgcolor: '#F76338', textTransform: 'none', fontWeight: 600, borderRadius: '8px', '&:hover': { bgcolor: '#e5552c' } }}>
                  {saving ? 'Saving...' : 'Save banner'}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default FlashBannerSettings;
